import { createBrowserRouter, Navigate } from "react-router-dom";
import AppLayout from "~/single-page/layouts/AppLayout";
import { ROUTES } from "~/single-page/constants/routes";
import Home from "~/single-page/pages/Home";
import Account from "../pages/Account";
import MyInspections from "../pages/MyInspections";
import MyInspection from "../pages/MyInspection";
import Schedule from "../pages/Schedule"
import Payment from "../pages/Payment";
import PackagePayment from "../pages/PackagePayment";
import ProtectedRoute from "../../components/ProtectedRoute";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import VehicleInspections from '../api/vehicle_inspections';

// Redireciona /finalization/:id para /my_inspections/:id
const FinalizationRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`${ROUTES.MY_INSPECTIONS}/${id}`} replace />;
};

const PaymentProtectedRoute = ({ children }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resp = await VehicleInspections.getById(id);
        const inspection = resp?.inspection ?? resp;

        if (!inspection) throw new Error("Inspection vazia");

        const status =
          inspection?.status ??
          inspection?.payment?.status ??
          "waiting_payment";

        if (status === "paid" && !cancelled) {
          navigate(ROUTES.MY_INSPECTIONS, {
            replace: true,
            state: { error: "Esta inspeção já foi paga" },
          });
        }
      } catch (error) {
        console.error("Erro ao verificar status da inspeção:", error);
        if (!cancelled) {
          navigate(ROUTES.MY_INSPECTIONS, {
            replace: true,
            state: { error: "Erro ao carregar dados da inspeção" },
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, navigate]);

  if (loading) return null;
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

const InspectionProtectedRoute = ({ children }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resp = await VehicleInspections.getById(id);
        const inspection = resp?.inspection ?? resp;

        if (!inspection) throw new Error("Inspection vazia");

        const status =
          inspection?.status ??
          inspection?.payment?.status ??
          "waiting_payment";

        if (status === "processing" && !cancelled) {
          navigate(ROUTES.MY_INSPECTIONS, {
            replace: true,
            state: {
              warningTitle: "Pagamento em processamento",
              warning:
                "Seu pagamento está sendo processado. Assim que for aprovado, daremos sequência à geração da vistoria.",
            },
          });
        }
      } catch (error) {
        console.error("Erro ao verificar status da inspeção:", error);
        if (!cancelled) {
          navigate(ROUTES.MY_INSPECTIONS, {
            replace: true,
            state: { error: "Erro ao carregar dados da inspeção" },
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, navigate]);

  if (loading) return null;
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <AppLayout />,
      children: [
        {
          index: true,
          element: <Home />,
        },
        {
          path: ROUTES.ACCOUNT,
          element: (
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          ),
        },
        {
          path: ROUTES.MY_INSPECTIONS,
          element: (
            <ProtectedRoute>
              <MyInspections />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ROUTES.MY_INSPECTIONS}/:id`,
          element: (
            <InspectionProtectedRoute>
              <MyInspection />
            </InspectionProtectedRoute>
          ),
        },
        {
          path: `${ROUTES.SCHEDULE}/:id`,
          element: (
            <ProtectedRoute>
              <Schedule />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ROUTES.PAYMENT}/:id`,
          element: (
            <PaymentProtectedRoute>
              <Payment />
            </PaymentProtectedRoute>
          ),
        },
        {
          path: ROUTES.PACKAGE_PAYMENT,
          element: (
            <ProtectedRoute>
              <PackagePayment />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ROUTES.FINALIZATION}/:id`,
          element: (
            <ProtectedRoute>
              <FinalizationRedirect />
            </ProtectedRoute>
          ),
        },
      ],
    }
  ],
  {
    future: {
      v7_skipActionErrorRevalidation: true,
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_partialHydration: true,
      v7_normalizeFormMethod: true,
      v7_fetcherPersist: true
    }
  }
);
